import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Button, Field, Segmented, Sheet } from '../ui'
import { flagFromIso, COUNTRIES } from '../lib/placeEmoji'
import { defaultDirection, directionsFor, routeFromForm, type Direction } from '../lib/flightRoute'
import { secondHomeAirports, secondHomeOf, useProfile } from '../lib/profile'
import { homeName, useRegionsNow } from '../lib/regions'
import { yearFormMin } from '../lib/calendar'
import {
  learnedCountries, stayDraft, stayFromDraft, withCountry, withPlace, type StayDraft,
} from '../lib/placeCountry'

export type StayInput = {
  kind: 'home2' | 'travel'
  starts_on: string
  ends_on: string
  label: string | null
  provisional: boolean
  country: string | null
}

export type FlightInput = {
  on_date: string
  depart_at: string | null
  /** `null`: an end Doug left as "elsewhere" (a2-27). */
  from_iata: string | null
  to_iata: string | null
  code: string
}

export type VisitorInput = {
  who: string
  starts_on: string
  ends_on: string
  at_mine: boolean
}

type Kind = 'day' | 'stay' | 'flight' | 'visitor'

/** Every control Enter can move between, in document order. Buttons are
 *  not in it: Enter on a button is that button's own click. */
const FIELDS = 'input:not([type=hidden]):not(:disabled), select:not(:disabled)'

/**
 * Enter moves to the next field, and in the last one submits. Without the
 * first half a `<form>`'s implicit submission fires from *any* text field —
 * Enter in *Who* would add a visitor arriving and leaving today before the
 * dates were touched. A textarea is left alone (Enter is its newline), and
 * so is a key still composing (a dead key, an IME).
 */
function enterMovesOn(ev: KeyboardEvent<HTMLFormElement>) {
  if (ev.key !== 'Enter' || ev.nativeEvent.isComposing) return
  const t = ev.target
  if (!(t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) return
  const fields = [...ev.currentTarget.querySelectorAll<HTMLElement>(FIELDS)]
  const i = fields.indexOf(t)
  if (i < 0) return
  ev.preventDefault()
  if (i < fields.length - 1) fields[i + 1].focus()
  else ev.currentTarget.requestSubmit()
}

/**
 * A Year form as one scrolling column (WP 10.5): the fields, then the
 * buttons at the end of the column, then Delete for an edit — never a bar
 * pinned to the foot of the sheet.
 *
 * Doug's screenshot of the visitor form with the keyboard up: a blank page,
 * the date field, and *Add / Cancel*, with the field he was typing into
 * gone. A pinned bar is 68px the keyboard's shrunken viewport has to give
 * up before the fields get any, and it is the one thing in the sheet that
 * cannot scroll out of the way. In the column the buttons scroll with
 * everything else, and `Sheet`'s `focusSnapsToTop` keeps the focused field
 * in the middle of what is left.
 *
 * Exported for `Year.tsx`'s four edit forms (a day, a stay, a flight, a
 * visitor), which had the same bar and the same keyboard.
 */
export function FormColumn({ submitLabel, canSubmit = true, onSubmit, onCancel, onDelete, children }: {
  submitLabel: string
  canSubmit?: boolean
  onSubmit: () => void
  onCancel: () => void
  /** An edit's Delete, last and quiet, below the two that matter. */
  onDelete?: () => void
  children: ReactNode
}) {
  return (
    <form className="yr-form" noValidate onKeyDown={enterMovesOn}
      onSubmit={e => { e.preventDefault(); if (canSubmit) onSubmit() }}>
      {children}
      <div className="yr-form__acts">
        <Button type="submit" variant="primary" block disabled={!canSubmit}>{submitLabel}</Button>
        <Button variant="secondary" block onClick={onCancel}>Cancel</Button>
      </div>
      {onDelete && <Button variant="quiet" className="yr-form__del" onClick={onDelete}>Delete</Button>}
    </form>
  )
}

/**
 * a2-25 — a stay's fields, for the add sheet and `Year.tsx`'s edit form
 * alike. Doug chose: Type (his second home / Travel), Place, Country, with
 * Country filled from the place when it is known (`placeCountry.ts` says
 * what "known" is, and why nothing else is guessed). The select used to be
 * called Where while the place went in Label, so the field named for the
 * place was not the place.
 *
 * Country only for a trip: the second home carries its own flag. And Type
 * only for a person who has a second home (U1.3): everyone else has one
 * kind of stay, and a select with one option is a question with one answer.
 * An edit of a stay that *is* a second-home stay keeps the select, so it
 * can still be moved to Travel.
 */
export function StayFields({ d, onChange, second, learned, dateMin, dateMax, toMin }: {
  d: StayDraft
  onChange: (d: StayDraft) => void
  /** The profile's second home, or null: names the Type option and the Place hint. */
  second: { label: string } | null
  /** `learnedCountries(stays)`: the places Doug has already given a country. */
  learned: ReadonlyMap<string, string>
  dateMin: string
  dateMax: string | undefined
  /** To's floor: From for a new stay; nothing for an edit, which may be
   *  correcting a range typed backwards. */
  toMin: string | undefined
}) {
  return (
    <>
      {(second || d.kind === 'home2') && (
        <Field label="Type">
          <Field.Select value={d.kind} onChange={v => onChange({ ...d, kind: v as StayDraft['kind'] })} options={[
            { value: 'home2', label: second?.label.trim() || 'Second home' },
            { value: 'travel', label: 'Travel' },
          ]} />
        </Field>
      )}
      <Field label="From"><Field.Input type="date" value={d.start} min={dateMin} max={dateMax} onChange={e => onChange({ ...d, start: e.target.value })} /></Field>
      <Field label="To"><Field.Input type="date" value={d.end} min={toMin} max={dateMax} onChange={e => onChange({ ...d, end: e.target.value })} /></Field>
      <Field label="Place">
        <Field.Input value={d.place} placeholder={d.kind === 'travel' ? 'Rome' : second?.label.trim() || 'Second home'} autoCapitalize="words"
          onChange={e => onChange(withPlace(d, e.target.value, learned))} />
      </Field>
      {d.kind === 'travel' && (
        <Field label="Country">
          <Field.Select value={d.country} onChange={v => onChange(withCountry(d, v))} options={[
            { value: '', label: 'No flag' },
            ...COUNTRIES.map(([c, name]) => ({ value: c, label: `${flagFromIso(c)} ${name}` })),
          ]} />
        </Field>
      )}
      <Field label="Certainty">
        <Field.Select value={d.provisional ? '1' : ''} onChange={v => onChange({ ...d, provisional: !!v })} options={[
          { value: '', label: 'Confirmed' },
          { value: '1', label: 'Provisional' },
        ]} />
      </Field>
    </>
  )
}

/**
 * Y3's `+`: one sheet, a `Segmented` choosing what kind of thing this is,
 * the form for that kind beneath it.
 *
 * Replaces the three-row "what do you want to add" `Card` that used to open
 * a second sheet per choice — two taps and two sheets to add one day. The
 * field sets it switches between are the same ones the edit sheets for a
 * day, a stay and a flight still use (`Year.tsx`'s `CommitmentForm`,
 * `StayForm`, `FlightForm`); this does not replace those, because an edit
 * already knows its own kind and never needs to ask.
 *
 * WP 6.5 adds a fourth kind, Visitor — who, when they arrive and leave, and
 * whether they are staying at yours. It replaces "Someone visiting" as a
 * `Stay` kind: a visitor is not a place Doug went, and folding the two
 * together was the one-value-two-jobs bug every reader of `stays` had to
 * filter back out (`visitsIn`, now gone — see `PeriodCard`'s own header).
 */
export function YearAddSheet({ today, maxDate, stays, onClose, onAddDay, onAddStay, onAddFlight, onAddVisitor }: {
  today: string
  /** How far ahead the date pickers below reach — deliberately not the
   *  Year's drawn horizon (WP 5.9): `Year.tsx` passes its own longer,
   *  fixed bound so a calendar rule that shortens near 1 January cannot
   *  also stop a flight being entered for next year. */
  maxDate: string
  /** Doug's stays, for the places he has already given a country (a2-25). */
  stays: readonly { label: string | null; country: string | null }[]
  onClose: () => void
  onAddDay: (date: string, title: string) => void
  onAddStay: (s: StayInput) => void
  onAddFlight: (f: FlightInput) => void
  onAddVisitor: (v: VisitorInput) => void
}) {
  const [kind, setKind] = useState<Kind>('day')
  /** Where every date picker below starts: 1 January (a2-26), not today —
   *  a trip already taken has to be loggable for the year's stats to add up. */
  const from = yearFormMin(today)

  // Day
  const [date, setDate] = useState(today)
  const [title, setTitle] = useState('')

  // Stay or trip
  const profile = useProfile()
  const regions = useRegionsNow()
  const second = secondHomeOf(profile)
  const [stay, setStay] = useState(() => stayDraft(today, undefined, second ? 'home2' : 'travel'))
  const learned = useMemo(() => learnedCountries(stays), [stays])

  // Flight
  const [flightDate, setFlightDate] = useState(today)
  const [dir, setDir] = useState<Direction>(() => defaultDirection(profile.home_iata, secondHomeAirports(profile)))
  const [where, setWhere] = useState('')
  // a2-27: empty until set. A prefilled 18:45 was saved as a real departure
  // time on every flight Doug did not know the time of.
  const [time, setTime] = useState('')
  const [code, setCode] = useState('')

  // Visitor
  const [who, setWho] = useState('')
  const [arrives, setArrives] = useState(today)
  const [leaves, setLeaves] = useState(today)
  const [staying, setStaying] = useState<'mine' | 'else'>('mine')

  function save() {
    if (kind === 'day') { onAddDay(date, title.trim()); return }
    if (kind === 'stay') {
      onAddStay(stayFromDraft(stay))
      return
    }
    if (kind === 'visitor') {
      onAddVisitor({
        who: who.trim(),
        starts_on: arrives <= leaves ? arrives : leaves,
        ends_on: arrives <= leaves ? leaves : arrives,
        at_mine: staying === 'mine',
      })
      return
    }
    onAddFlight({ on_date: flightDate, depart_at: time || null, ...routeFromForm(dir, where), code: code.trim() || 'Flight' })
  }

  const disabled = (kind === 'day' && !title.trim()) || (kind === 'visitor' && !who.trim())

  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="Add to the year">
      <FormColumn submitLabel="Add" canSubmit={!disabled} onSubmit={save} onCancel={onClose}>
        <Segmented className="yr-form__kind" value={kind} onChange={v => setKind(v as Kind)} options={[
          { value: 'day', label: 'A day' },
          { value: 'stay', label: 'A stay or trip' },
          { value: 'flight', label: 'A flight' },
          { value: 'visitor', label: 'A visitor' },
        ]} />

        {kind === 'day' && (
          <>
            <Field label="Date"><Field.Input type="date" value={date} min={from} max={maxDate}
              onChange={e => setDate(e.target.value)} /></Field>
            <Field label="What"><Field.Input value={title} placeholder="Duck Fest" onChange={e => setTitle(e.target.value)} /></Field>
          </>
        )}

        {kind === 'stay' && (
          <StayFields d={stay} onChange={setStay} second={second} learned={learned} dateMin={from} dateMax={maxDate} toMin={stay.start} />
        )}

        {kind === 'flight' && (
          <>
            <Field label="Date"><Field.Input type="date" value={flightDate} min={from} max={maxDate} onChange={e => setFlightDate(e.target.value)} /></Field>
            <Field label="Direction">
              <Field.Select value={dir} onChange={v => setDir(v as Direction)}
                options={directionsFor({ homeIata: profile.home_iata, secondHome: second, homeName: homeName(regions, profile) })} />
            </Field>
            {dir.includes('*') && (
              <Field label="Where"><Field.Input value={where} placeholder="Lisbon" onChange={e => setWhere(e.target.value)} /></Field>
            )}
            <Field label="Time"><Field.Input type="time" value={time} onChange={e => setTime(e.target.value)} /></Field>
            <Field label="Flight number"><Field.Input value={code} placeholder="FR 1234" onChange={e => setCode(e.target.value)} /></Field>
          </>
        )}

        {kind === 'visitor' && (
          <>
            <Field label="Who"><Field.Input value={who} placeholder="Rachel" onChange={e => setWho(e.target.value)} /></Field>
            <Field label="Arrives"><Field.Input type="date" value={arrives} min={from} max={maxDate} onChange={e => setArrives(e.target.value)} /></Field>
            <Field label="Leaves"><Field.Input type="date" value={leaves} min={arrives} max={maxDate} onChange={e => setLeaves(e.target.value)} /></Field>
            <Field label="Staying">
              <Field.Select value={staying} onChange={v => setStaying(v as typeof staying)} options={[
                { value: 'mine', label: 'At yours' },
                { value: 'else', label: 'Elsewhere' },
              ]} />
            </Field>
          </>
        )}
      </FormColumn>
    </Sheet>
  )
}
